import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import {
  withLoading,
  useTranslator,
  useAppContext,
  mealPlaceholderImage,
  formatIsoDate,
  FormikInput as Input,
  formatIsoDateTime,
  useToast,
  callNativeMobileApp,
  callNativeAppRemoveLastRoute,
  // cloudflareImage,
} from '@front-activerse/common-components';
import { Container, Row, Col, Button, Spinner } from 'reactstrap';
import Layout from '../../Layout/Layout';
import TitleBar from '../../Layout/TitleBar';
import UniversalPlaceholder from '../../Layout/UniversalPlaceholder';
import MacroDonutChart from '../../Layout/Chart/MacroDonutChart';
import validationSchema from './validationSchema';
import { useNavigate } from 'react-router-dom';

interface ITrackerContainerProps {
  id: number;
  name: string;
  kcal: number;
  macro: {
    protein: {
      grams: number;
      percentage: number;
    };
    fat: {
      grams: number;
      percentage: number;
    };
    carbohydrates: {
      grams: number;
      percentage: number;
    };
  };
  preparationImageUrl: string;
  mealType: {
    type: number;
    percentage: number;
  };
  addMeal: (input: any) => Promise<any>;
}

const TrackerAddMealContainer: React.FC<ITrackerContainerProps> = ({
  id,
  name,
  kcal,
  macro,
  preparationImageUrl,
  mealType,
  addMeal,
}) => {
  const { t } = useTranslator();
  const { toastError, toastSuccess } = useToast();
  const navigate = useNavigate();
  const [showDateInput, setShowDateInput] = useState(false);

  const {
    consts: { APP_BRAND },
  } = useAppContext();

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const minDateObj = new Date(today);
  minDateObj.setDate(minDateObj.getDate() - 30);
  const minDate = formatIsoDate(minDateObj);

  const maxDateObj = new Date(today);
  maxDateObj.setDate(maxDateObj.getDate() + 30);
  const maxDate = formatIsoDate(maxDateObj);

  const getCurrentTime = () => {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const handleDateInputBlur = (value: string | number) => {
    // If date is still today's date, hide the input and show "Today" div
    if (value === formatIsoDate(new Date())) {
      setShowDateInput(false);
    } else {
      setShowDateInput(true);
    }
  };

  const renderMealImage = () => {
    let mealImg;
    if (preparationImageUrl) {
      mealImg = preparationImageUrl;
    } else {
      mealImg = mealPlaceholderImage(mealType.type, APP_BRAND as string);
    }
    // const coverImage = cloudflareImage(mealImg, `${APP_BRAND}`, 'mealPlanMealDetails');

    return <img src={mealImg} className="img-fluid rounded mb-3 mb-md-0" alt="" />;
  };

  const initialValues = {
    date: formatIsoDate(new Date()),
    time: getCurrentTime(),
    servings: 1,
  };

  const handleSubmit = async (values: any) => {
    try {
      const response = await addMeal({
        dateTime: formatIsoDateTime(values.date, `${values.time}:00`),
        mealId: id,
        sourceType: 'diet',
        portions: +values.servings,
      });

      if (response.__typename === 'DailyTrackerAddDietMealSuccess') {
        // refresh native tracker data
        callNativeMobileApp({
          action: 'refresh_data',
          payload: {
            module: 'tracker',
            date: values.date,
          },
        });

        navigate(-1);
        callNativeAppRemoveLastRoute();
        toastSuccess(<p>{t('meals.notification.trackerConfirmation.content')}</p>);
      } else {
        toastError();
      }
    } catch (error) {
      toastError();
    }
  };

  return (
    <Layout>
      <TitleBar showBackBtn={true}>{t('tracker.logMeal.navbarTitle')}</TitleBar>

      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({ isValid, isSubmitting, values }) => {
          // Helper function to format macro values
          const formatMacroDisplay = (originalGrams: number, originalPercentage: number) => {
            const multipliedGrams = Math.round(originalGrams * values.servings);

            // If multiplied grams is 0 but original percentage > 0, show "<1g"
            if (multipliedGrams === 0 && originalPercentage > 0) {
              return '<1';
            }

            return multipliedGrams;
          };

          // Calculate multiplied values based on servings
          const multipliedKcal = Math.round(kcal * values.servings);
          const multipliedMacro = {
            protein: {
              grams: formatMacroDisplay(macro.protein.grams, macro.protein.percentage),
            },
            fat: {
              grams: formatMacroDisplay(macro.fat.grams, macro.fat.percentage),
            },
            carbohydrates: {
              grams: formatMacroDisplay(macro.carbohydrates.grams, macro.carbohydrates.percentage),
            },
          };

          return (
            <Form>
              <div className="page-template-tracker">
                <Container className="py-md-4 full-height4">
                  <Row>
                    <Col xl={{ size: 10, offset: 1 }}>
                      <div className="page-container">
                        <div className="tracker-container">
                          <Row>
                            <Col md="6">{renderMealImage()}</Col>
                            <Col md="6">
                              <div className="d-flex">
                                <div className="me-1 position-relative">
                                  <Input
                                    name="date"
                                    type="date"
                                    min={minDate}
                                    max={maxDate}
                                    translateErrMsg
                                    callbackOnChange={handleDateInputBlur}
                                  />
                                  <div className={!showDateInput ? 'today' : 'd-none'}>
                                    {t('time.day.today')}
                                  </div>
                                </div>
                                <div className="me-1">
                                  <Input name="time" type="time" translateErrMsg />
                                </div>
                              </div>

                              <h2 className="title-large semi-bold mt-xxs mb-l">{name}</h2>

                              <div className="mb-xl">
                                <Input
                                  horizontal
                                  label={t('tracker.servings.title')}
                                  name="servings"
                                  type="number"
                                  translateErrMsg
                                  min={0.1}
                                  max={30}
                                  step={0.01}
                                  className="text-center"
                                />
                              </div>

                              <Row className="align-items-center">
                                <Col xs="4">
                                  <div className="macro-chart-container">
                                    <MacroDonutChart
                                      proteinPercentage={macro.protein.percentage}
                                      fatPercentage={macro.fat.percentage}
                                      carbsPercentage={macro.carbohydrates.percentage}
                                      missingPercentage={0}
                                      chartId="tracker-meal"
                                    />
                                    <div className="text">
                                      <p className="title-small semi-bold mb-0">{multipliedKcal}</p>
                                      <p className="description-small mb-0">kcal</p>
                                    </div>
                                  </div>
                                </Col>
                                <Col xs="8">
                                  {/* macrro */}
                                  <Row className="" noGutters>
                                    <Col>
                                      <div className="d-flex align-items-start">
                                        <div className="bullet protein"></div>
                                        <div>
                                          <p className="description-small mb-0">
                                            {t('tracker.macros.proteins')}
                                          </p>
                                          <p className="semi-bold mb-0">
                                            {multipliedMacro.protein.grams} g
                                          </p>
                                          <p className="description text-text-variant2-color mb-0">
                                            {macro.protein.percentage}%
                                          </p>
                                        </div>
                                      </div>
                                    </Col>
                                    <Col>
                                      <div className="d-flex align-items-start">
                                        <div className="bullet fat"></div>
                                        <div>
                                          <p className="description-small mb-0">
                                            {t('tracker.macros.fats')}
                                          </p>
                                          <p className="semi-bold mb-0">
                                            {multipliedMacro.fat.grams} g
                                          </p>
                                          <p className="description text-text-variant2-color mb-0">
                                            {macro.fat.percentage}%
                                          </p>
                                        </div>
                                      </div>
                                    </Col>
                                    <Col>
                                      <div className="d-flex align-items-start">
                                        <div className="bullet carbs"></div>
                                        <div>
                                          <p className="description-small mb-0">
                                            {t('tracker.macros.carbs')}
                                          </p>
                                          <p className="semi-bold mb-0">
                                            {multipliedMacro.carbohydrates.grams} g
                                          </p>
                                          <p className="description text-text-variant2-color mb-0">
                                            {macro.carbohydrates.percentage}%
                                          </p>
                                        </div>
                                      </div>
                                    </Col>
                                  </Row>
                                </Col>
                              </Row>
                            </Col>
                          </Row>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Container>

                <div className="sticky-footer">
                  <Container className="px-3">
                    <Row>
                      <Col xs="12" md={{ size: 4, offset: 4 }}>
                        <Button
                          type="submit"
                          color="primary"
                          className="w-100"
                          disabled={isSubmitting || !isValid}
                        >
                          {isSubmitting ? (
                            <>
                              <Spinner />
                              <span className="ms-2">{t('data-processing')}</span>
                            </>
                          ) : (
                            t('tracker.meal.logButton')
                          )}
                        </Button>
                      </Col>
                    </Row>
                  </Container>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </Layout>
  );
};

export default withLoading(TrackerAddMealContainer, UniversalPlaceholder);
